import {
  ConnectedPlatformLike,
  getConnectedPlatformTypeLabel,
} from '../template-and-pricing/utils/connectedPlatformLabel'

const formatChannelProductHeading = (platform?: ConnectedPlatformLike): string => {
  const channelDisplayName = getConnectedPlatformTypeLabel(platform)
  const channelName = `${platform?.display_name ?? ''}`.trim()
  if (!channelDisplayName || !channelName) return ''
  return `${channelDisplayName} - ${channelName} Product`
}

const configuredBundleQuantity = (relation: {
  quantity?: unknown
  platform_product_variant?: unknown
}): number | null => {
  const nestedVariant = relation?.platform_product_variant
  if (!nestedVariant || typeof nestedVariant !== 'object') return null

  const rawQuantity = relation?.quantity
  if (typeof rawQuantity === 'number' && Number.isFinite(rawQuantity)) return rawQuantity
  if (typeof rawQuantity === 'string' && rawQuantity.trim() !== '') {
    const parsed = Number(rawQuantity)
    if (Number.isFinite(parsed)) return parsed
  }
  return null
}

export const formatLinkedTypeLabel = (type?: string): string => {
  const normalized = `${type ?? ''}`.trim().toLowerCase()
  if (!normalized) return 'Channel'
  if (normalized === 'erp') return 'ERP'
  if (normalized === 'oms') return 'OMS'
  if (normalized === 'primary') return 'Source'
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

export const extractBundleRelationDataFromProductDetail = (productDetail: any): any[] => {
  const fullBundleRelationData = Array.isArray(productDetail?.varients_data_relation)
    ? productDetail.varients_data_relation
    : []

  return fullBundleRelationData.flatMap((relation: any) => {
    const relationVariant = relation?.platform_product_variant || relation
    if (!relationVariant) return []

    const relationBundleData = Array.isArray(relationVariant?.bundle_relation_data)
      ? relationVariant.bundle_relation_data
      : Array.isArray(relation?.bundle_relation_data)
        ? relation.bundle_relation_data
        : []

    return relationBundleData.length > 0 ? relationBundleData : []
  })
}

export type ProductLinkingContext = {
  platformProductDetail: any
  selectedVariant: any
  variantId?: string
  resolvedBundleRelationData: any[]
}

export const getConnectedPlatformDisplayName = (
  linkedItem: any,
  fetchedConnectedPlatformData: any[] = []
): string => {
  const linkedPlatformId = linkedItem?.platform_product?.platform
  const matchedPlatform = fetchedConnectedPlatformData?.find(
    (platform: any) => Number(platform?.id) === Number(linkedPlatformId)
  )

  return (
    matchedPlatform?.display_name ||
    matchedPlatform?.platform_info?.display_name ||
    linkedItem?.platform_product?.display_name ||
    linkedItem?.platform_product?.name ||
    linkedItem?.platform_info?.display_name ||
    ''
  )
}

export const getLinkedAvailablePlatformId = (
  linkedItem: any,
  fetchedConnectedPlatformData: any[] = [],
  fallbackAvailablePlatformId?: string
): string => {
  const linkedPlatformId = linkedItem?.platform_product?.platform
  const matchedPlatform = fetchedConnectedPlatformData?.find(
    (platform: any) => Number(platform?.id) === Number(linkedPlatformId)
  )

  const resolvedAvailablePlatformId =
    linkedItem?.platform_product?.available_platform_id ??
    linkedItem?.available_platform_id ??
    matchedPlatform?.platform_info?.id ??
    fallbackAvailablePlatformId

  return resolvedAvailablePlatformId ? String(resolvedAvailablePlatformId) : ''
}

export const getBundleRelationVariants = (
  linkedItem: any,
  ctx: ProductLinkingContext
): any[] => {
  if (formatLinkedTypeLabel(linkedItem?.type) !== 'Source') {
    return []
  }

  const productDetail: any = ctx.platformProductDetail
  const fullBundleRelationData = Array.isArray(productDetail?.varients_data_relation)
    ? productDetail.varients_data_relation
    : []
  const selectedVariantId = Number(ctx.selectedVariant?.id ?? ctx.variantId)
  const productLevelBundleRelationData =
    fullBundleRelationData.find((relation: any) => {
      const relationVariant = relation?.platform_product_variant || relation
      return Number(relationVariant?.id) === selectedVariantId
    })?.bundle_relation_data || []

  const bundleRelationSources = [
    linkedItem?.bundle_relation_data,
    ctx.selectedVariant?.bundle_relation_data,
    productLevelBundleRelationData,
    ctx.resolvedBundleRelationData,
  ]
  const bundleRelationData =
    bundleRelationSources.find((source) => Array.isArray(source) && source.length > 0) || []

  return bundleRelationData.filter(Boolean)
}

export const hasBundleRelationVariants = (linkedItem: any, ctx: ProductLinkingContext): boolean =>
  getBundleRelationVariants(linkedItem, ctx).length > 0

export type OrderedParentDetailRow = { item: any; index: number }

export const getOrderedParentDetails = (options: {
  productVariantDetail: any
  platformProductDetail: any
  selectedVariant: any
  variantId?: string
  platformId?: string
  mainStoreId?: number
  resolvedBundleRelationData: any[]
}): OrderedParentDetailRow[] => {
  const {
    productVariantDetail,
    platformProductDetail,
    selectedVariant,
    variantId,
    platformId,
    mainStoreId,
    resolvedBundleRelationData,
  } = options

  const ctx: ProductLinkingContext = {
    platformProductDetail,
    selectedVariant,
    variantId,
    resolvedBundleRelationData,
  }

  const parentDetails = Array.isArray(productVariantDetail?.parent_details)
    ? productVariantDetail.parent_details
    : []
  const fullBundleRelationVariants = extractBundleRelationDataFromProductDetail(platformProductDetail)
  const effectiveFullBundleRelationVariants =
    fullBundleRelationVariants.length > 0 ? fullBundleRelationVariants : resolvedBundleRelationData

  if (parentDetails.length === 0 && effectiveFullBundleRelationVariants.length > 0) {
    return [
      {
        item: {
          ...selectedVariant,
          type: 'Primary',
          isFullBundleRelationView: true,
          bundle_relation_data: effectiveFullBundleRelationVariants,
        },
        index: 0,
      },
    ]
  }

  return parentDetails
    .filter((item: any) => {
      if (mainStoreId && Number(platformId) === Number(mainStoreId)) {
        return item.type !== 'Primary'
      }
      return true
    })
    .map((item: any, index: number) => ({ item, index }))
    .sort((first, second) => {
      const firstHasBundle = hasBundleRelationVariants(first.item, ctx)
      const secondHasBundle = hasBundleRelationVariants(second.item, ctx)

      if (firstHasBundle !== secondHasBundle) {
        return firstHasBundle ? 1 : -1
      }

      return first.index - second.index
    })
}

export const getParentPlatformDisplayName = (
  linkedItem: any,
  fetchedConnectedPlatformData: any[] = []
): string => {
  const fromParentField = `${linkedItem?.parent_platform_display_name ?? ''}`.trim()
  if (fromParentField) {
    return fromParentField
  }

  return getConnectedPlatformDisplayName(linkedItem, fetchedConnectedPlatformData)
}

/** Human-readable linked platform name for sentences (SKU mismatch, bundle headers, etc.). */
export const getLinkedPlatformLabelForCopy = (
  linkedItem: any,
  fetchedConnectedPlatformData: any[] = [],
  fallback = 'linked platform'
): string => getParentPlatformDisplayName(linkedItem, fetchedConnectedPlatformData) || fallback

/** Listing-card eyebrow. Prefers "[channel display name] - [channel name] Product". */
export const getCurrentListingCardEyebrow = (
  platformDisplayName?: string,
  platform?: ConnectedPlatformLike
): string => {
  const channelHeading = formatChannelProductHeading(platform)
  if (channelHeading) return channelHeading

  const name = `${platformDisplayName ?? ''}`.trim() || 'Channel'
  return `${name.toUpperCase()} LISTING`
}

/** Linked-product eyebrow. Prefers "[channel display name] - [channel name] Product". */
export const getLinkedProductCardEyebrow = (
  linkedItem: any,
  fetchedConnectedPlatformData: any[] = []
): string => {
  const linkedPlatformId = linkedItem?.platform_product?.platform
  const matchedPlatform = fetchedConnectedPlatformData?.find(
    (platform: any) => Number(platform?.id) === Number(linkedPlatformId)
  )
  const channelHeading = formatChannelProductHeading(matchedPlatform)
  if (channelHeading) return channelHeading

  const platformName = getParentPlatformDisplayName(linkedItem, fetchedConnectedPlatformData)
  if (platformName) {
    return `${platformName.toUpperCase()} PRODUCT`
  }

  const typeLabel = formatLinkedTypeLabel(linkedItem?.type)
  if (typeLabel === 'Source' || typeLabel === 'OMS' || linkedItem?.platform_identity === 'PRIMARY') {
    return 'OMS PRODUCT'
  }

  return 'LINKED PRODUCT'
}

/** @deprecated Prefer getLinkedProductCardEyebrow for non-OMS linking UI */
export const getLinkedTargetListingLabel = (linkedItem: any, fetchedConnectedPlatformData: any[]): string => {
  const eyebrow = getLinkedProductCardEyebrow(linkedItem, fetchedConnectedPlatformData)
  return eyebrow
    .replace(/\s+OMS PRODUCT$/i, ' OMS product')
    .replace(/\s+LINKED PRODUCT$/i, ' Linked product')
}

export const normalizeSku = (sku?: string | null) => `${sku ?? ''}`.trim().toLowerCase()

export const resolveBundleComponentsFromRelation = (bundleRelationData: any[]): any[] => {
  if (!Array.isArray(bundleRelationData)) return []
  return bundleRelationData
    .map((relation: any) => {
      const variant = relation?.platform_product_variant || relation
      if (!variant) return null
      return {
        id: variant?.id ?? relation?.id,
        name: variant?.name || variant?.platform_product?.name || '',
        sku: variant?.sku || '',
        quantity: configuredBundleQuantity(relation),
        availableQuantity:
          variant?.available_quantity ??
          relation?.available_quantity ??
          variant?.inventory_quantity ??
          null,
      }
    })
    .filter(Boolean)
}
